/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";
import {
  Button,
  Description,
  Dropdown,
  Input,
  Label,
  Sidebar,
  Spacer,
  Switch,
  Text,
  theme,
} from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import { ARAttachedSlas, Sla, SLAUnitType, SupportHours } from "~/models";
import {
  useAxios,
  generateActionSuccessText,
  displayServiceHours,
  displaySlaType,
  displaySlaUnitType,
} from "~/tools";

const EditableFieldWrapper = styled.div`
  height: 3rem;
  display: flex;
  align-items: center;
`;

const supportHoursOptions = [
  {
    label: "Not Applicable",
    value: SupportHours.NOT_APPLICABLE,
  },
  { label: "8x5", value: SupportHours.H8D5 },
  { label: "8x7", value: SupportHours.H8D7 },
  { label: "24x5", value: SupportHours.H24D5 },
  { label: "24x7", value: SupportHours.H24D7 },
];

type Values = {
  comment: string;
  value: string;
  supportHours: string;
};

type Props = {
  refetch: () => void;
  sla: Sla | ARAttachedSlas["sla"];
  val: string;
  supportHours: SupportHours;
  comm: string;
  editURL: string;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function InlineEditSLA({
  refetch,
  sla,
  val,
  comm,
  supportHours,
  editURL,
}: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isDefaultValue, setIsDefaultValue] = React.useState(false);
  const [isDefaultSupportHours, setIsDefaultSupportHours] = React.useState(
    false
  );
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: Values) => {
    const organizeValues = {
      comment: values.comment,
      value: isDefaultValue ? null : Number(values.value),
      supportHours: isDefaultSupportHours ? null : values.supportHours,
    };

    try {
      await Axios().put(editURL, organizeValues);
      setIsSidebarOpen(false);
      refetch();
      showSuccess(generateActionSuccessText(`Sla`)()("edited")());
    } catch (e) {
      setIsSidebarOpen(false);
      showError(e?.response);
    }
  };

  React.useEffect(() => {
    setIsDefaultValue(sla?.range?.default === val);
  }, [sla?.range?.default, val]);

  React.useEffect(() => {
    setIsDefaultSupportHours(sla.supportHours === supportHours);
  }, [sla.supportHours, supportHours]);

  const { handleSubmit, register, errors, control } = useForm();

  const cancel = () => {
    setIsSidebarOpen(false);
    setIsDefaultValue(sla?.range?.default === val);
    setIsDefaultSupportHours(sla.supportHours === supportHours);
  };

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        type="button"
        size="sm"
        css={{ marginLeft: "auto" }}
        severity="medium"
        icon="edit"
      >
        Edit SLA
      </Button>
      <Sidebar
        title="Edit SLA"
        isOpen={isSidebarOpen}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        <Text tag="div" color={theme.color.text.text02}>
          Name:
        </Text>
        <Text weight="medium">{sla?.name}</Text>
        {sla?.description && (
          <>
            <Text tag="div" color={theme.color.text.text02}>
              Info:
            </Text>
            <Text weight="medium">{sla?.description}</Text>
          </>
        )}
        {sla?.priority && (
          <>
            <Text tag="div" color={theme.color.text.text02}>
              Priority level:
            </Text>
            <Text weight="medium">{sla?.priority}</Text>
          </>
        )}
        <Fieldset>
          <Label name="Support service hours" />
          <Switch
            position="right"
            labelText="Use default value"
            checked={isDefaultSupportHours}
            onChange={() => setIsDefaultSupportHours((value) => !value)}
          />
          <Spacer height={theme.spacing.spacing02} />

          {isDefaultSupportHours ? (
            <EditableFieldWrapper key="default">
              <Text size="md" tag="span" color={theme.colors.success}>
                {displayServiceHours(sla?.supportHours)}
              </Text>
            </EditableFieldWrapper>
          ) : (
            <EditableFieldWrapper key="supportHours">
              <Controller
                control={control}
                name="supportHours"
                defaultValue={supportHours}
                render={({ onChange, onBlur, value, name }) => (
                  <Dropdown
                    {...{ onChange, onBlur, name, value }}
                    options={supportHoursOptions}
                  />
                )}
              />
            </EditableFieldWrapper>
          )}
        </Fieldset>
        {sla?.slaType && (
          <>
            <Text tag="div" color={theme.color.text.text02}>
              Type:
            </Text>
            <Text weight="medium">{displaySlaType(sla?.slaType)}</Text>
          </>
        )}
        {sla?.unitType && (
          <>
            <Text tag="div" color={theme.color.text.text02}>
              Unit Type:
            </Text>
            <Text weight="medium">{displaySlaUnitType(sla?.unitType)}</Text>
          </>
        )}
        {sla.unitType === SLAUnitType.VALUE_RANGE && (
          <Fieldset>
            <Label name="Value" />
            <Text weight="medium" size="sm">
              Value has to be within range Min: {sla?.range?.min} Max:{" "}
              {sla?.range?.max} and Unit: {sla?.range?.unit}
            </Text>
            <Switch
              position="right"
              labelText="Use default value"
              checked={isDefaultValue}
              onChange={() => setIsDefaultValue((value) => !value)}
            />
            <Spacer height={theme.spacing.spacing02} />

            {isDefaultValue ? (
              <EditableFieldWrapper key="default">
                <Text size="md" tag="span" color={theme.colors.success}>
                  {sla?.range?.default}
                </Text>
              </EditableFieldWrapper>
            ) : (
              <EditableFieldWrapper key="value">
                <Input
                  type="number"
                  name="value"
                  defaultValue={val}
                  status={errors.value && "danger"}
                  placeholder="value"
                  ref={register({
                    required: true,
                    min: sla?.range?.min,
                    max: sla?.range?.max,
                  })}
                />
              </EditableFieldWrapper>
            )}
            {errors.value && (
              <Description>
                Value has to be within range Min: {sla?.range?.min} Max:{" "}
                {sla?.range?.max}
              </Description>
            )}
          </Fieldset>
        )}
        <Fieldset>
          <Label name="Comment" />
          <Input
            type="text"
            name="comment"
            defaultValue={comm}
            status={errors.comment && "danger"}
            placeholder="Type additional comment"
            ref={register}
          />
          {errors.comment && <Description>Comment is required.</Description>}
        </Fieldset>
        <FlexContainer>
          <Button onClick={handleSubmit(onSubmit)}>Save</Button>
          <Button
            severity="medium"
            type="button"
            css={{ marginLeft: "auto" }}
            onClick={cancel}
          >
            Cancel
          </Button>
        </FlexContainer>
      </Sidebar>
    </>
  );
}
